import React, { Suspense, useContext, useEffect, useState } from 'react';
import { AuthContext } from '@/contexts/authContextCore';
import { hasStoredSession } from '@/services/sessionHint';
import { SITE_MENU_BUTTON_CLASS, patchSiteAccount, useSiteAccount } from './siteAccountBridge';

// The account layer (auth, user menu, editor) is its own chunk: a visitor's page
// never pays for it.
const SiteAccountLayer = React.lazy(() => import('./SiteAccountLayer'));

const setSlotEl = (el: HTMLElement | null) => patchSiteAccount({ slotEl: el });
/** Longest the page waits for an idle moment before loading the layer for a stored session. */
const IDLE_TIMEOUT_MS = 3000;

/**
 * Where the user menu goes in the top bar: an empty slot the layer draws into,
 * and until it is up a plain sign-in button (a visitor who is not signed in
 * never loads it unless they press this).
 */
export const SiteAccountSlot: React.FC<{ compact: boolean }> = ({ compact }) => {
  const { menuReady, requested } = useSiteAccount();
  useEffect(() => { patchSiteAccount({ compact }); }, [compact]);
  return (
    <>
      <span ref={setSlotEl} />
      {!menuReady && (
        <button
          type="button"
          className={SITE_MENU_BUTTON_CLASS}
          style={{ touchAction: 'manipulation', minHeight: 44, minWidth: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          title={requested ? 'Loading…' : 'Sign in'}
          aria-label="Sign in"
          onClick={() => patchSiteAccount({ requested: true, openSignup: true })}
          onPointerEnter={() => { void import('./SiteAccountLayer'); }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
          </svg>
        </button>
      )}
    </>
  );
};

/**
 * Loads the account layer when it is wanted: always inside the full app (its
 * provider already exists), after the page is up and the browser idle for a
 * stored session, or when the visitor asks to sign in.
 */
export const SiteAccountLoader: React.FC<{ pageReady: boolean; orgSlug: string; slug?: string; domainOrgSlug?: string }> = ({ pageReady, orgSlug, slug, domainOrgSlug }) => {
  const inApp = useContext(AuthContext) !== undefined;
  const { requested } = useSiteAccount();
  const [idleReady, setIdleReady] = useState(false);
  const wantsSession = inApp || hasStoredSession();

  useEffect(() => {
    if (!pageReady || !wantsSession || idleReady) return undefined;
    const arm = () => setIdleReady(true);
    if ('requestIdleCallback' in window) {
      const handle = window.requestIdleCallback(arm, { timeout: IDLE_TIMEOUT_MS });
      return () => window.cancelIdleCallback(handle);
    }
    const timer = setTimeout(arm, 500);
    return () => clearTimeout(timer);
  }, [pageReady, wantsSession, idleReady]);

  // Leaving the page resets what it asked for (the next visit starts clean).
  useEffect(() => () => patchSiteAccount({ requested: false, openSignup: false, editing: false, menuReady: false, slotEl: null }), []);

  if (!requested && !(wantsSession && idleReady)) return null;
  return (
    <Suspense fallback={null}>
      <SiteAccountLayer orgSlug={orgSlug} slug={slug} domainOrgSlug={domainOrgSlug} />
    </Suspense>
  );
};
